// Weekly plan, holiday and local days (schedule.plan format "weekplan", schedule.pause_until and roast dates as local
// days, protocol §6). Local days and switch-on times are in the machine's time zone (profile.tz), FR-17.

export interface PlanWindow {
  /** minutes after midnight */
  on: number;
  /** minutes after midnight, null = no off time */
  off: number | null;
}

/** Monday..Sunday */
export type WeekPlan = PlanWindow[][];

export const PLAN_WINDOWS = 2;

/**
 * Switch-on to ready without measured cold starts: about 5 min (an Orione owner's review, research/overnight-review-
 * 2026-10-09.md: "Aufheizen in ~5 min"). The API learns the machine's own time from its cold starts (GET …/heatup).
 */
export const HEATUP_DEFAULT_S = 300;

/** The lead in whole minutes the schedule is shifted by (at least 1). */
export const leadMinutes = (seconds: number | null | undefined): number =>
  Math.max(1, Math.round((seconds ?? HEATUP_DEFAULT_S) / 60));

/**
 * The plan holds switch-on times (the firmware switches on at the time and heats afterwards); the app shows and takes
 * ready times (Dominik 21.10.2026, "Bereit um" as in the prototype). A ready time within the lead after midnight
 * switches on at 00:00: the plan has no day before it.
 */
export const readyOf = (on: number, lead: number): number => (on + lead) % 1440;
export const onFor = (ready: number, lead: number): number => Math.max(0, ready - lead);

const toMin = (x: string | undefined): number | null => {
  const m = /^(\d{1,2}):(\d{2})$/.exec(x ?? '');
  return m && Number(m[1]) < 24 && Number(m[2]) < 60 ? Number(m[1]) * 60 + Number(m[2]) : null;
};

export const hm = (min: number): string =>
  `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`;

/** "07:00-09:30,17:00;…" (7 days separated by ";", up to 2 windows by ","); "?" = not set yet (empty plan). */
export function parsePlan(text: unknown): WeekPlan {
  const days = String(text ?? '').split(';');
  if (days.length !== 7) return Array.from({ length: 7 }, () => []);
  return days.map((d) =>
    d
      .split(',')
      .filter(Boolean)
      .slice(0, PLAN_WINDOWS)
      .map((w) => {
        const [a, b] = w.split('-');
        return { on: toMin(a), off: toMin(b) };
      })
      .filter((w): w is PlanWindow => w.on !== null),
  );
}

export function planText(plan: WeekPlan): string {
  return plan
    .map((d) => d.map((w) => hm(w.on) + (w.off !== null && w.off !== w.on ? `-${hm(w.off)}` : '')).join(','))
    .join(';');
}

export const windowText = (w: PlanWindow): string =>
  hm(w.on) + (w.off !== null && w.off !== w.on ? `–${hm(w.off)}` : '');

/** The machine's time zone as an IANA name: the Orione's POSIX string is Germany; unknown strings: the browser's. */
export function zoneOf(posixTz: unknown): string | undefined {
  if (typeof posixTz !== 'string') return undefined;
  if (posixTz.startsWith('CET-1CEST')) return 'Europe/Berlin';
  if (posixTz === 'UTC0' || posixTz === 'UTC') return 'UTC';
  return undefined;
}

interface Wall {
  y: number;
  m: number;
  d: number;
  h: number;
  min: number;
  /** 0 Monday .. 6 Sunday */
  weekday: number;
}

function wallOf(ms: number, zone: string | undefined): Wall {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: zone,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hourCycle: 'h23',
    weekday: 'short',
  }).formatToParts(new Date(ms));
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '0';
  const weekdays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  return {
    y: Number(get('year')),
    m: Number(get('month')),
    d: Number(get('day')),
    h: Number(get('hour')),
    min: Number(get('minute')),
    weekday: Math.max(0, weekdays.indexOf(get('weekday'))),
  };
}

/** Local day (days since 1970-01-01) of a moment in the machine's zone. */
export function localDayOf(ms: number, zone: string | undefined): number {
  const w = wallOf(ms, zone);
  return Math.round(Date.UTC(w.y, w.m - 1, w.d) / 86_400_000);
}

/** "2026-10-18" (a date picker's value) -> local day; null for anything else. */
export function dayFromIso(iso: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return m ? Math.round(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) / 86_400_000) : null;
}

/** local day -> "2026-10-18" */
export function isoOfDay(day: number): string {
  return new Date(day * 86_400_000).toISOString().slice(0, 10);
}

/** local day -> "18.10." / "18 Oct" */
export function dayText(day: number, lang: 'de' | 'en'): string {
  return new Date(day * 86_400_000).toLocaleDateString(lang === 'de' ? 'de-DE' : 'en-GB', {
    day: '2-digit',
    month: lang === 'de' ? '2-digit' : 'short',
    timeZone: 'UTC',
  });
}

/** UTC ms of the start (00:00) of a local day in the zone. */
export function startOfLocalDay(day: number, zone: string | undefined): number {
  const d = new Date(day * 86_400_000);
  return msOfWall(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate(), 0, zone);
}

/** UTC ms of a wall time in the zone (two passes over the zone offset; DST gaps move forward). */
function msOfWall(y: number, m: number, d: number, minutes: number, zone: string | undefined): number {
  const naive = Date.UTC(y, m - 1, d, Math.floor(minutes / 60), minutes % 60);
  let guess = naive;
  for (let i = 0; i < 2; i++) {
    const w = wallOf(guess, zone);
    const asUtc = Date.UTC(w.y, w.m - 1, w.d, w.h, w.min);
    guess += naive - asUtc;
  }
  return guess;
}

/**
 * The next switch-on by the plan (nextOn() of the local app), skipping holiday days up to pause_until; null if the
 * schedule is off or nothing is planned within a week.
 */
export function nextSwitchOn(
  plan: WeekPlan,
  opts: { enabled: boolean; pauseUntil: number; now: number; zone: string | undefined },
): number | null {
  if (!opts.enabled) return null;
  for (let k = 0; k < 8; k++) {
    const day = wallOf(opts.now + k * 86_400_000, opts.zone);
    const local = Math.round(Date.UTC(day.y, day.m - 1, day.d) / 86_400_000);
    if (opts.pauseUntil && local <= opts.pauseUntil) continue;
    const ons = (plan[day.weekday] ?? []).map((w) => w.on).sort((a, b) => a - b);
    for (const on of ons) {
      const at = msOfWall(day.y, day.m, day.d, on, opts.zone);
      if (at > opts.now) return at;
    }
  }
  return null;
}
